import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Product } from '../services/types';
import DataService from '../services/dataService';

interface ProductContextType {
  products: Product[];
  addProduct: (product: Omit<Product, 'id' | 'rating' | 'reviews'>) => Promise<any>;
  updateProduct: (id: string | number, updates: Partial<Product>) => Promise<any>;
  deleteProduct: (id: string | number) => Promise<void>;
  getProductById: (id: string | number) => Product | undefined;
  getProductsBySeller: (sellerId: string) => Product[];
  refreshProducts: (filters?: {
    category?: string;
    searchTerm?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
    shopId?: string;
  }) => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [initialized, setInitialized] = useState(false);

  const transformDbProduct = (p: any): Product => ({
    id: p.id,
    name: p.name,
    category: p.category,
    price: p.price,
    unit: p.unit,
    rating: p.rating || 4.5,
    reviews: 0, // Placeholder
    image: p.image || 'https://images.unsplash.com/photo-1595855799321-4d10d0f77b78?auto=format&fit=crop&w=800',
    seller: {
      id: p.seller?.id || p.seller_id || 'unknown',
      name: p.seller?.name || 'Seller',
      location: p.seller?.county || 'Kenya',
      rating: p.seller?.rating || 4.8,
      verified: p.seller?.verified || false,
      joinedDate: p.seller?.created_at || '2023-01-01',
      responseRate: '98%'
    },
    stock: p.stock,
    description: p.description,
    features: p.features || [],
    tags: p.tags || [],
    isNew: false,
    isBestSeller: false,
    shop_id: p.shop_id,
    shop_slug: p.shop_slug,
    shop_name: p.shop_name
  } as any);

  const refreshProducts = async (filters?: {
    category?: string;
    searchTerm?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
    shopId?: string;
  }) => {
    try {
      const dbProducts = await DataService.getProducts(filters);
      if (filters) {
        const mapped = (dbProducts || []).map((p: any) => {
          if (p.seller && typeof p.seller === 'object' && p.seller.name && !p.name) return p as Product;
          return transformDbProduct(p);
        });
        setProducts(mapped);
        return;
      }

      if (dbProducts && dbProducts.length > 0) {
        const mappedProducts = dbProducts.map((p: any) => {
          if (p.seller && typeof p.seller === 'object' && p.seller.name && !p.name) return p as Product;
          return transformDbProduct(p);
        });
        setProducts(mappedProducts);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Failed to refresh products:', error);
      if (!filters) setProducts([]);
    }
  };

  // Load on mount
  useEffect(() => {
    refreshProducts().then(() => setInitialized(true));
  }, []);

  const addProduct = async (newProductData: Omit<Product, 'id' | 'rating' | 'reviews'>) => {
    try {
      // Prepare data for Supabase
      const dbData = {
        name: newProductData.name,
        category: newProductData.category,
        price: newProductData.price,
        unit: newProductData.unit,
        stock: newProductData.stock,
        description: newProductData.description,
        image: newProductData.image,
        seller_id: (newProductData as any).seller_id || (newProductData.seller as any)?.id,
        shop_id: (newProductData as any).shop_id
      };

      const createdProduct = await DataService.addProduct(dbData);
      await refreshProducts();
      return createdProduct;
    } catch (error) {
      console.error('Error adding product to DB:', error);
      throw error;
    }
  };

  const updateProduct = async (id: string | number, updates: Partial<Product>) => {
    try {
      // Prepare data for Supabase
      const dbUpdates: any = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.price !== undefined) dbUpdates.price = updates.price;
      if (updates.unit !== undefined) dbUpdates.unit = updates.unit;
      if (updates.stock !== undefined) dbUpdates.stock = updates.stock;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.image !== undefined) dbUpdates.image = updates.image;

      const updatedProduct = await DataService.updateProduct(id, dbUpdates);
      await refreshProducts();
      return updatedProduct;
    } catch (error) {
      console.error('Error updating product in DB:', error);
      throw error;
    }
  };

  const deleteProduct = async (id: string | number) => {
    try {
      await DataService.deleteProduct(id);
      await refreshProducts();
    } catch (error) {
      console.error('Error deleting product from DB:', error);
      throw error;
    }
  };

  const getProductById = (id: string | number) => {
    return products.find((p) => p.id.toString() === id.toString());
  };

  const getProductsBySeller = (sellerId: string) => {
    return products.filter((p) => p.seller.id === sellerId);
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        addProduct,
        updateProduct,
        deleteProduct,
        getProductById,
        getProductsBySeller,
        refreshProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
